import { Fragment, useEffect, useState } from 'react'
import clsx from 'clsx'
import { ChevronDown, ChevronRight, X } from 'lucide-react'
import {
  buildTripStopSequence,
  fetchTuplGeotags,
  fetchTuplRouteStops,
  istDateOf,
  todayIst,
  type TuplGeotagEvent,
  type TuplTripLogRow,
  type TripStopSequence,
} from './useTuplApi'

function hhmm(ts: number | null): string {
  if (ts == null) return '—'
  return new Date(ts).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata',
  })
}

function minutes(sec: number | null | undefined): string {
  return sec == null ? '—' : String(Math.round(sec / 60))
}

function statusLabel(r: TuplTripLogRow): { text: string; color: string } {
  if (r.is_dead) return { text: 'Dead', color: 'text-slate-500' }
  if (r.is_lost) return { text: 'Lost', color: 'text-rose-600' }
  if (r.is_short) return { text: 'Short', color: 'text-amber-600' }
  if (!r.completed) return { text: 'Open', color: 'text-sky-600' }
  return { text: 'Completed', color: 'text-emerald-600' }
}

function TripSequenceBody({
  vehicleId,
  row,
}: {
  vehicleId: string
  row: TuplTripLogRow
}): JSX.Element | null {
  const [seq, setSeq] = useState<TripStopSequence | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    setLoading(true)
    setErr(null)
    // The row's own day, not always today -- this modal's rows are not guaranteed to be
    // today's (the caller may show a wider window), so geotags must be fetched for the
    // day the trip actually happened on or the time-window filter in
    // buildTripStopSequence matches nothing.
    const day = row.started_ts != null
      ? istDateOf(row.started_ts)
      : row.ended_ts != null ? istDateOf(row.ended_ts) : todayIst()
    const route = row.route_code || ''
    Promise.all([
      route ? fetchTuplRouteStops(route, row.direction || undefined) : Promise.resolve([] as Awaited<ReturnType<typeof fetchTuplRouteStops>>),
      fetchTuplGeotags(vehicleId, day),
    ])
      .then(([stops, geotags]) => {
        if (!live) return
        setSeq(buildTripStopSequence({
          stops,
          geotags,
          startedTs: row.started_ts,
          endedTs: row.ended_ts,
          isShort: !!row.is_short,
          expectedDirection: row.direction,
        }))
      })
      .catch((e: unknown) => {
        if (!live) return
        setErr(e instanceof Error ? e.message : 'Failed to load stop sequence')
        setSeq(null)
      })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [vehicleId, row.trip_id, row.route_code, row.direction, row.started_ts, row.ended_ts, row.is_short])

  if (loading) {
    return <p className="px-4 py-2 text-[11px] text-slate-500">Loading stop sequence…</p>
  }
  if (err) {
    return <p className="px-4 py-2 text-[11px] text-rose-600">{err}</p>
  }
  if (!seq) return null

  return (
    <div className="space-y-2 border-t border-slate-100 bg-slate-50/80 px-4 py-3" data-testid="trip-stop-sequence">
      {seq.stamped.length === 0 && !seq.breakReason && seq.further.length === 0 ? (
        <p className="text-[11px] text-slate-500">No geotagged stops in this trip window.</p>
      ) : null}
      {seq.stamped.length > 0 && (
        <ol className="space-y-1">
          {seq.stamped.map((s) => (
            <li key={`${s.seq}-${s.name}`} className="flex items-baseline gap-2 text-[11px] text-slate-700">
              <span className="w-8 shrink-0 font-mono tabular-nums text-slate-400">{s.seq}</span>
              <span className="min-w-0 flex-1 truncate font-medium">{s.name}</span>
              <span className="shrink-0 font-mono tabular-nums text-slate-500">{hhmm(s.ts)}</span>
            </li>
          ))}
        </ol>
      )}
      {seq.breakReason && (
        <p
          role="status"
          className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] font-semibold text-amber-800"
        >
          {seq.breakReason}
        </p>
      )}
      {seq.further.length > 0 && (
        <div>
          <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Further geotags
          </p>
          <ul className="space-y-1">
            {seq.further.map((g: TuplGeotagEvent, i) => (
              <li key={`${g.ts}-${i}`} className="flex items-baseline gap-2 text-[11px] text-slate-600">
                <span className="shrink-0 font-mono tabular-nums text-slate-400">{hhmm(g.ts)}</span>
                <span className="min-w-0 flex-1 truncate">{g.name || g.kind}</span>
                {g.seq != null ? (
                  <span className="shrink-0 font-mono text-slate-400">seq {g.seq}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * A vehicle's full day of stops/trips, opened from the board's row timeline button.
 * Deliberately fed by the exact same rows /api/tupl/reports/trips returns for this
 * vehicle_id -- the Duty log report and this modal must never be able to disagree
 * about what a given trip's expected vs actual duration was.
 *
 * Phase B: expand a trip row for stamped stop sequence until break → banner → further geotags.
 */
export function TuplVehicleTimelineModal({ vehicleId, rows, loading, onClose }: {
  vehicleId: string
  rows: TuplTripLogRow[]
  loading: boolean
  onClose: () => void
}): JSX.Element {
  const [expanded, setExpanded] = useState<string | null>(null)

  useEffect(() => {
    const esc = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])

  const sorted = [...rows].sort((a, b) => (a.started_ts ?? 0) - (b.started_ts ?? 0))

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-900/25 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${vehicleId} timeline`}
        className={clsx(
          'flex max-h-[min(88vh,720px)] w-full max-w-3xl flex-col overflow-hidden',
          'animate-slide-up rounded-lg border border-slate-200 bg-white shadow-2xl',
        )}
      >
        <header className="flex shrink-0 items-start gap-3 border-b border-slate-200/80 px-6 py-4">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              Today's stops &amp; trips
            </div>
            <h2 className="mt-1 truncate text-sm font-semibold text-slate-900">{vehicleId}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-slate-500 shadow-sm ring-1 ring-inset ring-slate-300 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto">
          {loading ? (
            <p className="px-6 py-8 text-center text-sm text-slate-500">Loading…</p>
          ) : sorted.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-slate-500">No trips recorded for this vehicle today.</p>
          ) : (
            <table className="w-full text-left text-[12px]">
              <thead className="sticky top-0 bg-slate-50 text-[10.5px] uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="w-8 px-2 py-2 font-medium" aria-label="Expand" />
                  <th className="px-4 py-2 font-medium">Started</th>
                  <th className="px-4 py-2 font-medium">Ended</th>
                  <th className="px-4 py-2 font-medium">Route</th>
                  <th className="px-4 py-2 font-medium">Dir</th>
                  <th className="px-4 py-2 font-medium">Schedule</th>
                  <th className="px-4 py-2 text-right font-medium">Sched min</th>
                  <th className="px-4 py-2 text-right font-medium">Actual min</th>
                  <th className="px-4 py-2 text-right font-medium">Km</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((r) => {
                  const st = statusLabel(r)
                  const open = expanded === r.trip_id
                  return (
                    <Fragment key={r.trip_id}>
                      <tr className="text-slate-700">
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            aria-expanded={open}
                            aria-label={open ? 'Collapse stop sequence' : 'Expand stop sequence'}
                            onClick={() => setExpanded(open ? null : r.trip_id)}
                            className="flex h-6 w-6 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                          >
                            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-900">{hhmm(r.started_ts)}</td>
                        <td className="whitespace-nowrap px-4 py-2">{hhmm(r.ended_ts)}</td>
                        <td className="px-4 py-2">{r.route_code || 'Route entry pending'}</td>
                        <td className="px-4 py-2">{r.direction || '—'}</td>
                        <td className="px-4 py-2">{r.schedule_code || '—'}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{minutes(r.duration_sec)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{minutes(r.actual_duration_sec)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {r.distance_gps_km != null ? r.distance_gps_km.toFixed(1) : '—'}
                        </td>
                        <td className={clsx('px-4 py-2 font-medium', st.color)}>{st.text}</td>
                      </tr>
                      {open ? (
                        <tr>
                          <td colSpan={10} className="p-0">
                            <TripSequenceBody vehicleId={vehicleId} row={r} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
